import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { NoticeTone } from './Notice';
import './Patterns.css';

export interface OverlayAlert {
  id: string;
  message: string;
  tone: NoticeTone;
}

interface AlertHostValue {
  alerts: OverlayAlert[];
  push: (message: string, tone?: NoticeTone) => string;
  dismiss: (id: string) => void;
}

const AlertHostContext = createContext<AlertHostValue | null>(null);

let nextAlertId = 0;

/**
 * Owns the queue of alerts that must stay visible regardless of what else is
 * on screen. Provided once, near the top of the app.
 */
export const AlertHostProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<OverlayAlert[]>([]);
  // Mirrors `alerts` synchronously so `push` can read the current queue and
  // return the right id in the SAME tick — `setAlerts`'s functional updater
  // runs on React's schedule, not immediately, so it cannot be read back
  // right after the call.
  const alertsRef = useRef<OverlayAlert[]>([]);

  // De-duplicate: pushing a message identical to one already queued (same
  // message AND tone) must not add a second entry — it returns the existing
  // alert's id instead. Without this, a route that keeps re-triggering the
  // same failing fetch piles up one identical notice per visit for the rest
  // of the session (nothing here ever clears the queue on its own).
  const push = useCallback((message: string, tone: NoticeTone = 'danger') => {
    const dupe = alertsRef.current.find((a) => a.message === message && a.tone === tone);
    if (dupe) return dupe.id;
    const id = `alert-${nextAlertId++}`;
    alertsRef.current = [...alertsRef.current, { id, message, tone }];
    setAlerts(alertsRef.current);
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    alertsRef.current = alertsRef.current.filter((a) => a.id !== id);
    setAlerts(alertsRef.current);
  }, []);

  const value = useMemo(() => ({ alerts, push, dismiss }), [alerts, push, dismiss]);
  return <AlertHostContext.Provider value={value}>{children}</AlertHostContext.Provider>;
};

export function useOverlayAlerts(): Pick<AlertHostValue, 'push' | 'dismiss'> {
  const ctx = useContext(AlertHostContext);
  if (!ctx) throw new Error('useOverlayAlerts must be used inside an AlertHostProvider');
  return { push: ctx.push, dismiss: ctx.dismiss };
}

/**
 * Renders the queued alerts through a portal, so they are siblings of the
 * modal layer rather than descendants of whatever page pushed them. Nothing
 * here dismisses on a timer: an alert this surface carries is one the reader
 * has to act on, so it leaves when they say so.
 */
export const AlertHost: React.FC = () => {
  const ctx = useContext(AlertHostContext);
  const { t } = useTranslation();
  if (!ctx || ctx.alerts.length === 0) return null;

  const node = (
    <div className="pat-alert-host" data-testid="alert-host" role="alert" aria-live="assertive">
      {ctx.alerts.map((a) => (
        <div key={a.id} className={`pat-notice pat-notice--${a.tone} pat-alert-host__item`}>
          <span>{a.message}</span>
          <button
            type="button"
            className="pat-alert-host__dismiss"
            data-testid="alert-host-dismiss"
            onClick={() => ctx.dismiss(a.id)}
            aria-label={t('patterns.alertHost.dismissLabel')}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
};
